/**
 * MobileSettingsView
 *
 * Mobile-optimized settings view that replaces the desktop SettingsView layout
 * when isMobile === 2.
 * Provides a glassmorphic sticky header with a title row, an optional add button,
 * and a natively scrollable row of chip-style tab buttons for navigating between
 * settings menu items.
 *
 * NOTE: MUI Tabs variant="scrollable" is replaced with a plain horizontal scroll
 * container of MUI Chips so that touch-based scrolling works reliably on mobile.
 */
import React, { useRef, useEffect } from "react";
import { Box, IconButton, Tooltip, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object}   props
 * @param {Array}    props.menuList       - Array of menu item label strings
 * @param {number}   props.selectedMenu   - Currently active tab index
 * @param {Function} props.onMenuChange   - Called with new tab index when switching
 * @param {Function} props.onAddAction    - Called when the add button is clicked
 * @param {boolean}  props.showAddButton  - Whether to render the add button
 * @param {React.ReactNode} props.children - Content rendered below the header
 */
export default function MobileSettingsView({
  menuList,
  selectedMenu,
  onMenuChange,
  onAddAction,
  showAddButton,
  children,
}) {
  const scrollRef = useRef(null);
  const chipRefs = useRef([]);

  // Scroll the selected chip into view when selectedMenu changes
  useEffect(() => {
    const container = scrollRef.current;
    const activeChip = chipRefs.current[selectedMenu];
    if (container && activeChip) {
      const containerRect = container.getBoundingClientRect();
      const chipRect = activeChip.getBoundingClientRect();
      const scrollLeft =
        chipRect.left -
        containerRect.left -
        containerRect.width / 2 +
        chipRect.width / 2 +
        container.scrollLeft;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, [selectedMenu]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mt: 0 }}>
      {/* ---- Glassmorphic sticky header ---- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          bgcolor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(24px) saturate(1.5)",
          WebkitBackdropFilter: "blur(24px) saturate(1.5)",
          borderBottom: "1px solid rgba(0,40,128,0.06)",
          boxShadow:
            "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
          px: 1.5,
          pt: 0.75,
          pb: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
          transition: TRANSITION_SMOOTH,
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, rgba(255,144,21,0.18), transparent)",
          },
        }}
      >
        {/* Title row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 48,
            px: 0.5,
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              fontSize: "1.15rem",
              color: "rgb(0,0,0)",
              pl: 0.5,
              letterSpacing: "-0.025em",
              transition: "color 0.2s ease",
            }}
          >
            <Langs str="Settings" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {showAddButton && (
              <Tooltip title={<Langs str="Add" />}>
                <IconButton
                  onClick={onAddAction}
                  size="large"
                  sx={iconButtonSx}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Scrollable chip-tabs row — native CSS overflow for reliable touch */}
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            width: "100%",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            px: 0.5,
            py: 1,
            pb: 1.25,
          }}
        >
          {menuList.map((item, index) => {
            const isSelected = index === selectedMenu;
            return (
              <Chip
                key={index}
                ref={(el) => (chipRefs.current[index] = el)}
                label={<Langs str={item} />}
                onClick={() => onMenuChange(index)}
                sx={{
                  flexShrink: 0,
                  height: 36,
                  borderRadius: "18px",
                  fontWeight: isSelected ? 700 : 500,
                  fontSize: "0.8rem",
                  letterSpacing: "-0.01em",
                  cursor: "pointer",
                  transition: TRANSITION_SMOOTH,
                  bgcolor: isSelected
                    ? "rgba(255,144,21,0.12)"
                    : "rgba(0,0,0,0.04)",
                  color: isSelected ? "#FF8701" : "rgba(0,0,0,0.55)",
                  border: isSelected
                    ? "1.5px solid rgba(255,144,21,0.35)"
                    : "1.5px solid transparent",
                  boxShadow: isSelected
                    ? "0 2px 8px rgba(255,144,21,0.15)"
                    : "none",
                  "&:hover": {
                    bgcolor: isSelected
                      ? "rgba(255,144,21,0.16)"
                      : "rgba(0,0,0,0.07)",
                  },
                  "&:active": {
                    transform: "scale(0.96)",
                  },
                  "& .MuiChip-label": {
                    px: 1.5,
                    whiteSpace: "nowrap",
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* ---- Page content ---- */}
      <Box sx={{ px: 1.5, pt: 1, pb: 2 }}>{children}</Box>
    </Box>
  );
}
